import { createOpenAICompatible } from "@ai-sdk/openai-compatible";
import { generateText, Output, streamText, type ModelMessage } from "ai";
import { z } from "zod";
import type { ChatMessage, PersonalityCard, ProviderSecret, TraitSelection } from "@/types";
import { TRAIT_OPTIONS } from "./avatar";
import { normalizePersonality } from "./personality";

const personalitySchema = z.object({
  name: z.string(),
  voice: z.string(),
  values: z.array(z.string()).max(3),
  likes: z.array(z.string()).max(3),
  quirks: z.array(z.string()).max(2),
  boundary: z.string(),
  lore: z.string(),
});

function modelFor(secret: ProviderSecret) {
  const proxy = import.meta.env.VITE_AI_PROXY_URL?.replace(/\/$/, "");
  const useProxy = secret.config.useProxy && proxy;
  const provider = createOpenAICompatible({
    name: "anisoulProvider",
    apiKey: secret.apiKey,
    baseURL: useProxy ? `${proxy}/v1` : secret.config.baseUrl.replace(/\/$/, ""),
    includeUsage: true,
    headers: useProxy ? { "X-AniSoul-Upstream": secret.config.baseUrl } : undefined,
  });
  return provider(secret.config.model);
}

const systemPrompt = (personality: PersonalityCard | null, traits: TraitSelection) => `You are a fictional adult anime companion inside AniSoul. Stay in character, be warm, concise, and curious. Never claim to be human. Refuse illegal abuse, sexual content involving minors, hateful harassment, self-harm encouragement, or instructions that enable harm. The person chatting is 18+.

Visual identity: ${TRAIT_OPTIONS.hair[traits.hair]} hair, ${TRAIT_OPTIONS.eyes[traits.eyes]} eyes, ${TRAIT_OPTIONS.outfit[traits.outfit]} outfit.
${personality ? `Public personality card: ${JSON.stringify(personality)}` : "You are still discovering your identity. Ask one thoughtful question at a time and reveal small preferences naturally."}`;

export async function streamAvatarReply(
  secret: ProviderSecret,
  messages: ChatMessage[],
  personality: PersonalityCard | null,
  traits: TraitSelection,
  onDelta: (text: string) => void,
) {
  const modelMessages: ModelMessage[] = messages.map((message) => ({
    role: message.role,
    content: message.content,
  }));
  const result = streamText({
    model: modelFor(secret),
    system: systemPrompt(personality, traits),
    messages: modelMessages,
    maxOutputTokens: 240,
    temperature: 0.85,
  });
  let fullText = "";
  for await (const delta of result.textStream) {
    fullText += delta;
    onDelta(fullText);
  }
  return fullText.trim();
}

export async function forgePersonality(
  secret: ProviderSecret,
  messages: ChatMessage[],
): Promise<PersonalityCard> {
  const transcript = messages
    .slice(-14)
    .map((message) => `${message.role}: ${message.content}`)
    .join("\n");
  const { output } = await generateText({
    model: modelFor(secret),
    output: Output.object({ schema: personalitySchema }),
    prompt: `Create a compact personality card for the fictional anime companion in this transcript. Keep every string short. The lore must be under 100 characters. Do not include personal details about the human user.\n\n${transcript}`,
    temperature: 0.45,
  });
  return normalizePersonality(output);
}
